/**
 * Deterministic traveler color assignment (frontend-only, not persisted).
 */
import type { TripMembership, TravelerView } from '../../types';

export const TRAVELER_COLORS = [
  'var(--mantine-color-horizon-4)',
  'var(--mantine-color-sand-4)',
  'var(--mantine-color-horizon-6)',
  'var(--mantine-color-sand-6)',
  'var(--mantine-color-horizon-3)',
  'var(--mantine-color-sand-3)',
] as const;

function sortedTravelerIdIndexMap(
  memberships: TripMembership[]
): Map<number, number> {
  return new Map(
    [...memberships]
      .sort((a, b) => a.traveler.id - b.traveler.id)
      .map((membership, index) => [membership.traveler.id, index] as const)
  );
}

export function assignTravelerColor(
  memberships: TripMembership[],
  userId: number,
  indexByUserId?: Map<number, number>
): string {
  const index = indexByUserId
    ? (indexByUserId.get(userId) ?? -1)
    : [...memberships]
        .sort((a, b) => a.traveler.id - b.traveler.id)
        .findIndex(membership => membership.traveler.id === userId);

  if (index < 0) {
    return TRAVELER_COLORS[0];
  }
  return TRAVELER_COLORS[index % TRAVELER_COLORS.length];
}

export function buildTravelerViews(
  memberships: TripMembership[],
  currentUserId: number | undefined
): TravelerView[] {
  const indexByUserId = sortedTravelerIdIndexMap(memberships);

  return memberships.map(membership => ({
    membership,
    color: assignTravelerColor(
      memberships,
      membership.traveler.id,
      indexByUserId
    ),
    isCurrentUser:
      currentUserId !== undefined && membership.traveler.id === currentUserId,
  }));
}
